import * as Sentry from '@sentry/react'
import { createRoot } from 'react-dom/client'
import { PostHogProvider } from 'posthog-js/react'
import './i18n'
import './index.css'
import App from './App.tsx'

// Initialize Sentry for error monitoring
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,
    beforeSend(event, hint) {
      const error = hint.originalException
      // Ignore Mapbox AbortError (already handled below)
      if (error instanceof Error && error.name === 'AbortError') {
        return null
      }
      // Ignore ResizeObserver errors (browser bug)
      if (event.message?.includes('ResizeObserver')) {
        return null
      }
      return event
    },
  })
}

// Suppress Mapbox AbortError when map is removed during tile loading
// This is a known issue: https://github.com/mapbox/mapbox-gl-js/issues/12226
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.name === 'AbortError') {
    event.preventDefault()
  }
})

// Google Analytics 4 initialization
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID
if (GA_MEASUREMENT_ID) {
  // Load gtag.js script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)

  // Initialize gtag (must be on window for gtag.js to work)
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  window.gtag = function (..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', GA_MEASUREMENT_ID)
}

// Posthog Analytics initialization
const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-11-30',
} as const

createRoot(document.getElementById('root')!).render(
  <PostHogProvider
    apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
    options={posthogOptions}
  >
    <App />
  </PostHogProvider>,
)
